import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { CommonCodeColumn, CompCdColumn, WarehouseColumn } from 'src/utils/grid/GridColumnContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { MST3010_R01Dto } from './Dto_MST3010_R01';
import { useMST3010_R01Store } from './Store_MST3010_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST3010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST3010_R01Dto> | null>;
	defaultMST3010_R01Row: MST3010_R01Dto;
}

export const Table_MST3010_R01 = (props: Props) => {
	const { gridRef, defaultMST3010_R01Row } = props;

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		MST3010_R01List,
		selectedMST3010_R01,
		setSelectedMST3010_R01,
		initSelectedMST3010_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useMST3010_R01Store();

	const { compCdList, workProcessList, wmsCdList, warehouseCdList } = useCommonCodeStore();

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'workplaceCd',
		gridData: MST3010_R01List,
		defaultGridData: defaultMST3010_R01Row,
		selectedRow: selectedMST3010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		workplaceCd: '작업장코드',
		workplaceNm: '공정명',
		compCd: '사업장코드',
		displaySeq: '표시순서',
		workProcess: '작업공정',
		useYn: '사용여부',
		orderYn: '지시여부',
		barcodePrint: '바코드발행',
		levelCd: '레벨코드',
		outsourcingYn: '외주관리',
		scanHistory: '스캔이력여부',
		faultyYn: '불량관리',
		logicCenter: '기본물류',
		inWarehouse: '입고창고',
		outWarehouse: '출고창고',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'workplaceCd')(params), []);

	const baseColumnDefs: ColDef<MST3010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '작업장코드', colId: 'workplaceCd', field: 'workplaceCd', width: 90,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'workplaceCd'),
		},
		{
			headerName: '공정명', colId: 'workplaceNm', field: 'workplaceNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'workplaceNm'),
		},
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compCd'),
			...CompCdColumn.options(compCdList, false, undefined, undefined),
		},
		{
			headerName: '표시순서', colId: 'displaySeq', field: 'displaySeq', width: 70,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'displaySeq'),
		},
		{
			headerName: '작업공정', colId: 'workProcess', field: 'workProcess', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'workProcess'),
			valueFormatter: (p) => {
				const found = workProcessList?.find((opt) => opt.minorCd === p.value);
				return found ? found.codeNm : p.value;
			},
		},
		{
			headerName: '사용여부', colId: 'useYn', field: 'useYn', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'useYn'),
		},
		{
			headerName: '지시여부', colId: 'orderYn', field: 'orderYn', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'orderYn'),
		},
		{
			headerName: '바코드발행', colId: 'barcodePrint', field: 'barcodePrint', width: 90,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'barcodePrint'),
		},
		{
			headerName: '라벨코드', colId: 'levelCd', field: 'levelCd', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'levelCd'),
		},
		{
			headerName: '외주관리', colId: 'outsourcingYn', field: 'outsourcingYn', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'outsourcingYn'),
		},
		{
			headerName: '스캔이력여부', colId: 'scanHistory', field: 'scanHistory', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'scanHistory'),
		},
		{
			headerName: '불량관리', colId: 'faultyYn', field: 'faultyYn', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'faultyYn'),
		},
		{
			headerName: '기본물류', colId: 'logicCenter', field: 'logicCenter', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'logicCenter'),
			...CommonCodeColumn.options(wmsCdList, false, undefined, undefined),
		},
		{
			headerName: '출고창고', colId: 'outWarehouse', field: 'outWarehouse', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'outWarehouse'),
			...WarehouseColumn.options(
				warehouseCdList, false, (params) => params.data?.logicCenter, undefined, () => {},
			),
		},
		{
			headerName: '입고창고', colId: 'inWarehouse', field: 'inWarehouse', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'inWarehouse'),
			...WarehouseColumn.options(
				warehouseCdList, false, (params) => params.data?.logicCenter, undefined, () => {},
			),
		},
		{
			headerName: '불량창고', colId: 'badWarehouse', field: 'badWarehouse', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'badWarehouse'),
			...WarehouseColumn.options(
				warehouseCdList, false, (params) => params.data?.logicCenter, undefined, () => {},
			),
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'remark'),
		},
	], [getCellStyle, compCdList, workProcessList, wmsCdList, warehouseCdList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST3010_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST3010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST3010_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST3010_R01?.workplaceCd === rowData.workplaceCd;
		setSelectedMST3010_R01(isSelected ? undefined : rowData);
	}, [selectedMST3010_R01, setSelectedMST3010_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		workplaceCd: `${MST3010_R01List.length.toLocaleString()}건`,
		compCd: '',
		editable: false,
	}]), [MST3010_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [MST3010_R01List, selectedMST3010_R01]);

	return (
		<GridContextWrapper<MST3010_R01Dto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedMST3010_R01}
			defaultRowData={defaultMST3010_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST3010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST3010_R01Dto>({
					idKey: 'workplaceCd',
					selectedList: selectedMST3010_R01 ? [selectedMST3010_R01.workplaceCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
